import { Outlet, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { MessageSquareMore } from "lucide-react";

import { api } from "@constructionautomation/backend";
import { AppLink } from "@/components/app-link";
import { PageHeader } from "@/components/layout";
import { RevisionBadge } from "@/components/semantic-badges";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRouteParam } from "@/lib/use-route-param";
import { formatDate } from "@/lib/utils";
import { useProject } from "@/components/projects";

export const Route = createFileRoute("/revCollab")({
  component: RevCollabLayout,
});

function RevCollabLayout() {
  const documentId = useRouteParam("documentId");
  const revisionId = useRouteParam("revisionId");

  if (documentId || revisionId) {
    return <Outlet />;
  }

  return <RevCollabPage />;
}

export function RevCollabPage() {
  const { projectId, isLoading: isProjectLoading } = useProject();
  const { data: documents, isPending, error } = useQuery({
    ...convexQuery(api.revCollab.listHubDocuments, { projectId: projectId! }),
    enabled: !!projectId,
  });

  if (error) {
    throw error;
  }

  return (
    <div className="space-y-4">
      <PageHeader title="CollabPilot" />

      {isProjectLoading || isPending || !documents ? (
        <Card>
          <CardContent className="py-10 text-sm text-muted-foreground">
            Loading documents...
          </CardContent>
        </Card>
      ) : documents.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-sm text-muted-foreground">
            No supported PDF or image revisions found.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {documents.map((document) => (
            <Card key={document.documentId} className="transition-colors hover:border-primary">
              <CardHeader className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="line-clamp-2 text-base">{document.documentName}</CardTitle>
                  <MessageSquareMore className="size-4 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">{document.projectName}</p>
              </CardHeader>
              <CardContent className="space-y-3 text-xs text-muted-foreground">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" size="sm" className="tabular-nums">
                    {document.supportedRevisionCount}{" "}
                    {document.supportedRevisionCount === 1 ? "Revision" : "Revisions"}
                  </Badge>
                  <RevisionBadge number={document.latestRevisionNumber} latest />
                </div>
                <p>Recent activity {formatDate(document.recentActivityAt)}</p>
                <AppLink
                  to="/revCollab/documents/$documentId"
                  params={{ documentId: document.documentId }}
                  title={`CollabPilot · ${document.documentName}`}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Open document
                </AppLink>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
